import Image from 'next/image';

import { useMediaQuery } from '@/hooks/shared/useMediaQuery';

import theme from '@/styles/theme';

type SizeProps = {
  width: number;
  height: number;
};

export type LogoProps = {
  src: string;
  pcSize: SizeProps;
  mobileSize: SizeProps;
};

type Props = {
  label: string;
  logo?: LogoProps;
  onClick?: (label: string) => void;
};

/**
 * - 구글맵에 표시할 마커 컴포넌트입니다
 * - 이미지만 처리 가능한 기본 마커와 다르게, 컴포넌트 형태로 마커를 표시할 수 있습니다
 * @param {string} label - 마커에 표시할 텍스트
 * @param {object} logo - 마커에 표시할 로고 이미지 객체 (src, pcSize, mobileSize)
 * @param {function} onClick - 마커 클릭시 실행할 함수
 * @returns
 */
export default function CustomMarker({ label, logo, onClick }: Props) {
  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme`screens.lg`})`,
  });

  const onClickHandler = (e: any) => {
    e.stopPropagation(); // 마커 뒤의 지도 아이콘 클릭 방지
    if (label) onClick?.(label);
  };

  const logoWidth = logo
    ? isDesktop
      ? logo.pcSize.width
      : logo.mobileSize.width
    : 0;
  const logoHeight = logo
    ? isDesktop
      ? logo.pcSize.height
      : logo.mobileSize.height
    : 0;

  return (
    // ::after 는 마커 하단의 삼각형 핀. lg 에서 굵기만 커지고 색은 그대로다.
    <button
      type="button"
      aria-label={label}
      disabled={!onClick}
      onClick={onClickHandler}
      className="relative flex items-center gap-[8px] rounded-[38px] bg-apps-corePrimary01 px-[16px] py-[8px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.15)] after:absolute after:left-1/2 after:top-full after:-translate-x-1/2 after:border-b-0 after:border-l-[7px] after:border-r-[7px] after:border-t-[10px] after:border-b-transparent after:border-l-transparent after:border-r-transparent after:border-t-apps-corePrimary01 after:content-[''] lg:px-[20px] lg:py-[12px] lg:after:border-l-[11px] lg:after:border-r-[11px] lg:after:border-t-[16px]"
    >
      {logo && (
        // 로고 크기는 호출부가 주는 런타임 숫자라 클래스로 만들 수 없어 style 로 넘긴다.
        // 원본과 동일하게 값이 falsy 면 아예 선언하지 않는다.
        <div
          className="relative flex items-center justify-center"
          style={{
            width: logoWidth ? `${logoWidth}px` : undefined,
            height: logoHeight ? `${logoHeight}px` : undefined,
          }}
        >
          {/* sizez 속성은 최대 100px 아이콘으로 가정하고 임의로 설정하였음 */}
          <Image
            src={logo.src}
            alt="logo"
            fill
            sizes="400px"
            style={{
              objectFit: 'contain',
            }}
          />
        </div>
      )}
    </button>
  );
}

// const CustomMarkerText = styled.div`
//   font-size: 14px;
//   font-weight: 500;
//   line-height: 150%;
//   font-family: Pretendard; // 구글 지도 기본 폰트가 적용되는 경우가 있어서 추가
//   color: ${theme`colors.apps.coreGrayWhite`};
//   @media (min-width: ${theme`screens.lg`}) {
//     font-size: 20px;
//   }
// `;
